/**
 * Router Guards
 *
 * Global navigation guards for authentication and authorization
 */
import { START_LOCATION, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore, useEntitlementsStore } from '@/stores';
import { SETTINGS_STEP_PATHS, SETTINGS_WELCOME_PATH, SETTINGS_CONFIGURE_BASE, bridgelinkEnabled } from '@/modules/settings/wizardSteps';

import { useUIStore } from '@/stores';
const INVALID_ROLE_VALUES = new Set(['NO_ROLE', 'DEFAULT', 'DEFAULT_ROLE']);

function hasNoAccessRole(parsedRoles: unknown): boolean {
  if (!Array.isArray(parsedRoles)) return true;

  const normalizedRoles = parsedRoles
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().toUpperCase())
    .filter((role) => role.length > 0);

  if (normalizedRoles.length === 0) return true;

  return normalizedRoles.every((role) => INVALID_ROLE_VALUES.has(role));
}

/**
 * Authentication Guard
 * Redirects to login if route requires authentication and user is not authenticated.
 * Also validates that the known access-token expiry (in-memory token, or the
 * /auth/session-check result after a page reload) has not passed — if it has, a token
 * refresh is attempted before proceeding.
 */
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  // Direct entry to /login (e.g. an invite email link opened on a shared
  // workstation) while a session already exists — send the user to a
  // dedicated page (ActiveSessionView) that signs the previous user out
  // automatically and then moves on to the login form, instead of silently
  // redirecting home or ending the session here. `from === START_LOCATION` is only true on the
  // first navigation after a page load, which distinguishes this from
  // in-app navigation (e.g. the browser Back button after signing in) —
  // that case must keep the "redirect home" behavior below.
  // Match on the route NAME, not the literal path: Vue Router's default non-strict,
  // case-insensitive matching resolves '/login/' and '/LOGIN' to the login route
  // while leaving `to.path` as typed, so a path comparison would let those
  // variants render the login form over the existing session.
  if (to.name === 'login' && from === START_LOCATION && authStore.isAuthenticated) {
    next({ name: 'active-session', query: to.query });
    return;
  }

  // If the access-token expiry is known (SV-563: from the in-memory token post-login, or
  // from /auth/session-check after a page reload), validate it hasn't passed before any
  // guard logic runs.
  if (authStore.hasActiveSession) {
    const exp = authStore.sessionExp;
    if (exp !== null && Math.floor(Date.now() / 1000) >= exp) {
      // Access token is expired — attempt a silent refresh.
      // On an invalid session, refreshAccessToken() clears auth and redirects to /login;
      // cancel this navigation. On a transient failure (e.g. 429) the user stays signed
      // in, so let the navigation continue.
      const refreshed = await authStore.refreshAccessToken();
      if (!refreshed && !authStore.isAuthenticated) {
        next(false);
        return;
      }
    }
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return path only — fullPath is intentionally excluded
    // to prevent tokens, IDs, or PII in query strings from leaking into browser
    // history, server access logs, and Referer headers on the /login page.
    next({
      path: '/login',
      query: { redirect: to.path },
    });
    return;
  }

  // Block access to authenticated routes until first-time MFA setup is complete
  if (
    authStore.isAuthenticated &&
    authStore.mfaSetupRequired &&
    to.path !== '/mfa-setup' &&
    // The /login redirect above lands here; without this exemption a session
    // with a pending MFA setup would be bounced straight into that user's MFA
    // flow, skipping the sign-out-or-continue choice.
    to.name !== 'active-session'
  ) {
    next({ path: '/mfa-setup', replace: true });
    return;
  }

  // For authenticated users accessing any authenticated route, check if they have
  // no valid role. This applies to URL-based navigation as well as programmatic.
  if (
    authStore.isAuthenticated
    && requiresAuth
    && to.path !== '/no-access'
    && to.path !== '/logout'
    && hasNoAccessRole(authStore.parsedRoles)
  ) {
    next({ path: '/no-access', replace: true });
    return;
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    // Redirect to home if already authenticated
    next({ path: '/' });
    return;
  }

  next();
}

/**
 * Role Guard
 * Checks if user has required role for the route, and blocks any role
 * listed in `excludedRoles` (e.g. a route open to everyone except Auditor).
 */
export function roleGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const requiredRole = to.meta.requiresRole as string | undefined;
  const excludedRoles = to.meta.excludedRoles as string[] | undefined;

  const uiStore = useUIStore();

  if (requiredRole && !authStore.hasRole(requiredRole)) {
    // User doesn't have required role
    next({ path: uiStore.getLastAuthorizedRoute(), replace: true });
  } else if (excludedRoles && authStore.hasOnlyRoles(excludedRoles)) {
    // User is restricted to roles explicitly excluded from this route
    next({ path: uiStore.getLastAuthorizedRoute(), replace: true });
  } else {
    next();
  }
}

/**
 * Logout Guard
 * Handles logout route
 */
export function logoutGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (to.path === '/logout') {
    const authStore = useAuthStore();
    authStore.clearAuth();
    next({ path: '/login' });
  } else {
    next();
  }
}

/**
 * Entitlements Guard
 * Checks if user has the required entitlement(s) for the route.
 *
 * Route meta options:
 *   - `requiredEntitlement: string`  — single entitlement key
 *   - `requiredEntitlements: string[]` — ALL must be granted
 *   - `requiredAnyEntitlement: string[]` — ANY must be granted
 */
export async function entitlementsGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const entitlementsStore = useEntitlementsStore();

  const uiStore = useUIStore();
  const lastAuthorizedRoute = uiStore.getLastAuthorizedRoute();
  const single = to.meta.requiredEntitlement as string | undefined;
  const all = to.meta.requiredEntitlements as string[] | undefined;
  const any = to.meta.requiredAnyEntitlement as string[] | undefined;

  const routeRequiresEntitlements = !!(single || all || any);
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);

  // For entitlement-protected routes, load entitlements before deciding access.
  if (authStore.isAuthenticated && requiresAuth && routeRequiresEntitlements && !entitlementsStore.isLoaded) {
    await entitlementsStore.fetchEntitlements();
  }

  // If entitlements haven't loaded yet, trigger a fetch whenever the user is
  // authenticated — regardless of whether this specific route requires them.
  // Without this, landing on an entitlement-free route (e.g. "/") after a hard
  // refresh, new tab, or the OIDC full-page reload would leave the sidebar
  // permanently empty because nobody else calls fetchEntitlements().
  if (!entitlementsStore.isLoaded) {
    if (authStore.isAuthenticated && requiresAuth && routeRequiresEntitlements) {
      // For entitlement-protected authenticated routes, load entitlements
      await entitlementsStore.fetchEntitlements();
    } else if (authStore.isAuthenticated && to.name !== 'active-session') {
      // Non-blocking: start the fetch so the sidebar updates reactively,
      // but do not delay the navigation for routes that don't require auth.
      //
      // Skipped on /active-session: that page exists to sign the stored session
      // out, so it must not make API calls — or trigger a silent token refresh
      // via the 401 handler — under the previous user's credentials.
      entitlementsStore.fetchEntitlements().catch(() => { /* non-fatal */ });
    }

    // If still not loaded after fetch attempt (or user not authenticated), deny access
    // to routes that explicitly require entitlements
    if (routeRequiresEntitlements && !entitlementsStore.isLoaded) {
      next({ path: lastAuthorizedRoute, replace: true });
      return;
    }
  }

  if (single && !entitlementsStore.hasEntitlement(single)) {
    next({ path: lastAuthorizedRoute, replace: true });
    return;
  }

  if (all && !entitlementsStore.hasAllEntitlements(all)) {
    next({ path: lastAuthorizedRoute, replace: true });
    return;
  }

  if (any && !entitlementsStore.hasAnyEntitlement(any)) {
    next({ path: lastAuthorizedRoute, replace: true });
    return;
  }

  next();
}

/**
 * Settings Onboarding Guard
 *
 * After authentication, determines whether the tenant has completed first-time
 * settings setup using only the auth store and localStorage:
 *   - `is_settings_configured` is returned by GET /users/:username on login
 *     (auth.store.ts's `_finishLogin()`/`setAuth()`) and persisted to
 *     localStorage. No separate GET API call is made by this guard.
 *   - If `settingsOnboarded === false`, redirects every non-wizard route to the
 *     checklist landing page (`/app-settings/welcome`) so the user must complete
 *     the required steps before using the rest of the app.
 *   - Navigation between the 10 wizard step paths themselves is free-form — the
 *     user may jump to any step in any order; there is no sequential gate and no
 *     leave-blocking dialog.
 */
export async function settingsOnboardingGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  // Only applies to authenticated users
  if (!authStore.isAuthenticated) {
    return next();
  }

  // Only enforce the wizard for routes that are part of the authenticated SPA.
  // Public routes (login, healthcheck, 404 catch-all) and any external URL
  // that falls through to the Vue router must not be redirected to the
  // settings wizard — the user hasn't been asked to log in for those paths.
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);
  if (!requiresAuth) {
    return next();
  }

  // Routes explicitly opted out of the wizard gate (e.g. /healthcheck, /:pathMatch(.*)*,
  // or any future utility/container route that should never be redirected to the wizard
  // even if it inherits requiresAuth: true from a parent record).
  if (to.meta.skipSettingsGuard) {
    return next();
  }

  // NOTE: the checklist page is exempt from the redirect below, but that exemption
  // must come *after* the resolution block — not before it.  On a cold load of
  // /app-settings/welcome (hard refresh, bookmark, restored tab) that route is the
  // first navigation of the session, so returning early here would leave
  // `settingsOnboarded` at null: an in-progress tenant would see the first-visit
  // splash with its real progress ignored, and an already-configured tenant would
  // see the splash at all (its "Begin Configuration" button then writes onboarding
  // state onto a tenant whose is_settings_configured is already true).

  // Lazy import to avoid circular dependency
  const { useSettingsStore } = await import('@/stores/settings.store');
  const settingsStore = useSettingsStore();

  // Resolve the configured flag once per session.
  // Source of truth: localStorage (written from the GET /users/:username
  // response at login and kept in sync by markSettingsConfigured). No GET
  // API call is made by this guard itself.
  if (settingsStore.settingsOnboarded === null) {
    // Lazy import, like useSettingsStore above: a static import of
    // onboardingProgress.ts's dependency on app.config.ts (which reads
    // import.meta.env) would make every consumer of guards.ts, including
    // specs that don't mock @/core/config, fail to load under Jest.
    // Delegates to the shared resolver so this and checkSettingsOnboarding()'s
    // fallback chain can never drift apart — see onboardingProgress.ts.
    const { resolveIsSettingsConfigured } = await import('@/modules/settings/onboardingProgress'); // LOCAL_ONBOARDING_OVERRIDE: revert to an inline localStorage/JWT chain using readIsSettingsConfigured()
    const resolved = await resolveIsSettingsConfigured(authStore.isSettingsConfigured);
    if (resolved === null) {
      // Neither localStorage, the auth store's value, nor (if the local
      // override is active) the API resolved anything yet — allow navigation
      // so the app is not blocked on startup (e.g. hard refresh before login
      // completes).
      return next();
    }
    settingsStore.resolveOnboardingState(resolved);
  }

  // Configured tenants have nothing to see on the checklist page — bounce them to
  // the app.  (The view's own onMounted redirect can't cover this: on a cold load
  // it would mount before anything resolved the flag.)
  if (settingsStore.settingsOnboarded === true && to.path === SETTINGS_WELCOME_PATH) {
    return next({ path: '/', replace: true });
  }

  // Settings already configured — no restrictions
  if (settingsStore.settingsOnboarded !== false) {
    return next();
  }

  // ── First-time setup is required ──────────────────────────────────────

  // The checklist landing page is always reachable while unconfigured.  Must
  // precede the -1 bounce below, which would otherwise loop welcome -> welcome.
  if (to.path === SETTINGS_WELCOME_PATH) {
    return next();
  }

  // The stepper page (/app-settings/configure/<key>) is a second, equally valid
  // way to walk the same 10 steps — see settingsConfigureRoute in
  // modules/settings/router.ts. It isn't in SETTINGS_STEP_PATHS (those are the
  // standalone page paths), so it needs its own check here.
  const isConfigureStepPath = to.path.startsWith(`${SETTINGS_CONFIGURE_BASE}/`);
  const targetStepIndex = SETTINGS_STEP_PATHS.indexOf(to.path);

  if (targetStepIndex === -1 && !isConfigureStepPath) {
    // Any non-wizard target (the settings dashboard, or anywhere else in the app)
    // bounces silently to the checklist — no dialog, no distinction between
    // in-session and fresh navigation.
    return next({ path: SETTINGS_WELCOME_PATH, replace: true });
  }

  // Within the wizard: free-form navigation. The user may jump to any of the
  // 10 step paths in any order.
  return next();
}

/**
 * BridgeLink Guard
 *
 * There's no separate entitlement for interoperability — whether
 * 'interoperability-config' exists at all (checklist entry, stepper slot,
 * dashboard card, and this route itself) is decided solely by the tenant's
 * live bridgelink_enabled flag (GET /assets). This guard resolves that flag
 * into wizardSteps.ts's shared `bridgelinkEnabled` ref on every settings-module
 * navigation — so nextStepPath/previousStepPath/firstIncompleteStep and every
 * view reading it agree — and blocks direct navigation to the
 * interoperability-config route (standalone or stepper) while it's disabled,
 * since WIZARD_STEPS-based route registration can't itself be made
 * conditional on an async flag.
 */
export async function bridgelinkGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated || !to.path.startsWith('/app-settings')) {
    return next();
  }

  // Lazy import, like settingsOnboardingGuard above: avoids a static
  // @/core/config dependency chain for specs that don't mock it.
  const { useSettingsStore } = await import('@/stores/settings.store');
  const settingsStore = useSettingsStore();
  await settingsStore.ensureBridgelinkAssets();
  bridgelinkEnabled.value = settingsStore.bridgelinkAssets?.bridgelink_enabled === true;

  if (!bridgelinkEnabled.value && to.path.endsWith('/interoperability-config')) {
    return next({ path: '/app-settings/dashboard', replace: true });
  }

  return next();
}
